import React, { useCallback, useMemo, useState } from 'react';
import { Platform, Pressable, ScrollView, StyleSheet, TextInput, View } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import useResponsiveness from '@/hooks/useResponsiveness';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { paletteForSlug } from '@/utils/agent_catalog';

const isWeb = Platform.OS === 'web';

// Horizontal padding for the hero and spotlight. Matches the
// `CatalogBrowseShell` body indent (15 on XS, 20 otherwise) so the headline,
// search field, spotlight, toolbar, and grid all share one left edge. The
// category scroller on XS bleeds to the screen edges using the negative of
// this so a partially visible trailing tile reads as a scroll affordance.
export const TOOLS_HERO_PAD_X = { xs: 15, sm: 20, lg: 20 };

const CategoryTile = React.memo(function CategoryTile({
  label, icon, palette, active, onPress, colors, isXS, neutral = false,
}) {
  const [hovered, setHovered] = useState(false);
  const fg = neutral ? colors.text_color_primary : palette.fg;
  const bg = neutral ? colors.solid_color_tertiary : palette.bg;
  return (
    <Pressable
      onPress={onPress}
      onHoverIn={() => setHovered(true)}
      onHoverOut={() => setHovered(false)}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={label}
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        gap: 9,
        height: isXS ? 38 : 42,
        paddingLeft: 6,
        paddingRight: isXS ? 12 : 14,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: active ? fg : hovered ? fg : colors.border_color_secondary,
        backgroundColor: active ? bg : colors.solid_color_block,
        ...(isWeb && {
          cursor: 'pointer',
          transition: 'border-color 0.16s ease, background-color 0.16s ease',
        }),
      }}
    >
      <View
        style={{
          width: isXS ? 26 : 30,
          height: isXS ? 26 : 30,
          borderRadius: 8,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: active ? fg : bg,
        }}
      >
        <Icon name={icon || 'grid-2'} size={isXS ? 12 : 13} color={active ? '#fff' : fg} />
      </View>
      <Text
        size={isXS ? 's' : 'n'}
        weight={active ? 'bold' : 'semibold'}
        numberOfLines={1}
        style={{ color: active ? fg : colors.text_color_primary }}
      >
        {label}
      </Text>
    </Pressable>
  );
});

/**
 * Tools catalog masthead: eyebrow + headline + subtitle, a prominent search
 * field, the "Request a tool" action, and category tiles as the primary way
 * to move around the catalog.
 *
 * All strings are supplied by the page so the same component serves the
 * root catalog and `/tools/category/[slug]`.
 */
function ToolsHero({
  eyebrow,
  headline,
  subtitle,
  /** Shown to the right of the search field on desktop, e.g. "161 tools". */
  countLabel = null,
  search,
  action = null,
  categories = [],
  activeCategorySlug = null,
  onCategoryChange,
  allLabel = 'All tools',
  /** Palette of the active category — tints the eyebrow. */
  accentPalette = null,
}) {
  const { colors } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const [focused, setFocused] = useState(false);

  const padX = isXS ? TOOLS_HERO_PAD_X.xs : isSM ? TOOLS_HERO_PAD_X.sm : TOOLS_HERO_PAD_X.lg;

  const styles = useMemo(() => StyleSheet.create({
    wrap: {
      width: '100%',
      paddingHorizontal: padX,
      paddingTop: isXS ? 18 : 30,
      paddingBottom: isXS ? 6 : 10,
    },
    topRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 16,
    },
    eyebrowRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      marginBottom: isXS ? 8 : 12,
    },
    eyebrowDot: {
      width: 6,
      height: 6,
      borderRadius: 3,
    },
    eyebrow: {
      letterSpacing: 1.4,
      textTransform: 'uppercase',
    },
    headline: {
      fontSize: isXS ? 30 : isSM ? 38 : 44,
      lineHeight: isXS ? 34 : isSM ? 42 : 48,
      letterSpacing: isXS ? -0.8 : -1.2,
      maxWidth: 820,
    },
    subtitle: {
      marginTop: isXS ? 8 : 12,
      fontSize: isXS ? 14 : 16,
      lineHeight: isXS ? 21 : 24,
      maxWidth: 640,
    },
    action: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      height: 38,
      paddingHorizontal: 14,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: colors.border_color_secondary,
      backgroundColor: colors.solid_color_block,
      flexShrink: 0,
      ...(isWeb && { cursor: 'pointer', transition: 'border-color 0.16s ease, background-color 0.16s ease' }),
    },
    actionHovered: {
      borderColor: colors.text_color_primary,
      backgroundColor: colors.solid_color_tertiary,
    },
    actionMobile: {
      alignSelf: 'flex-start',
      marginTop: 14,
      height: 36,
    },
    search: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 12,
      marginTop: isXS ? 18 : 26,
      height: isXS ? 48 : 56,
      paddingLeft: isXS ? 14 : 18,
      paddingRight: isXS ? 10 : 12,
      borderRadius: 14,
      borderWidth: 1,
      backgroundColor: colors.solid_color_block,
      maxWidth: 820,
      ...(isWeb && { transition: 'border-color 0.16s ease, box-shadow 0.16s ease' }),
    },
    searchInput: {
      flex: 1,
      minWidth: 0,
      fontSize: isXS ? 15 : 17,
      color: colors.text_color_primary,
      paddingVertical: 0,
      ...(isWeb && { outlineStyle: 'none' }),
    },
    searchCount: {
      paddingHorizontal: 8,
      fontVariant: ['tabular-nums'],
    },
    clearBtn: {
      width: 30,
      height: 30,
      borderRadius: 8,
      alignItems: 'center',
      justifyContent: 'center',
      ...(isWeb && { cursor: 'pointer' }),
    },
    tilesWrap: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 8,
      marginTop: isXS ? 14 : 20,
    },
    tilesScroll: {
      marginTop: 14,
      marginLeft: -padX,
      marginRight: -padX,
    },
    tilesScrollContent: {
      flexDirection: 'row',
      gap: 8,
      paddingLeft: padX,
      paddingRight: padX,
    },
  }), [colors, isXS, isSM, padX]);

  const showSearch = search && typeof search.onChangeText === 'function';
  const hasValue = !!(search?.value && String(search.value).length > 0);
  const showAction = typeof action?.onPress === 'function';

  const clearSearch = useCallback(() => search?.onChangeText?.(''), [search]);

  const tiles = useMemo(() => {
    const items = [
      <CategoryTile
        key="__all"
        label={allLabel}
        icon="grid-2"
        neutral
        palette={paletteForSlug('')}
        active={!activeCategorySlug}
        onPress={() => onCategoryChange?.(null)}
        colors={colors}
        isXS={isXS}
      />,
    ];
    for (const cat of categories) {
      if (!cat?.slug) continue;
      items.push(
        <CategoryTile
          key={cat.slug}
          label={cat.name || cat.slug}
          icon={cat.icon}
          palette={paletteForSlug(cat.slug)}
          active={activeCategorySlug === cat.slug}
          onPress={() => onCategoryChange?.(cat.slug)}
          colors={colors}
          isXS={isXS}
        />,
      );
    }
    return items;
  }, [categories, activeCategorySlug, onCategoryChange, allLabel, colors, isXS]);

  const eyebrowColor = accentPalette?.fg || colors.text_color_tertiary;

  const renderAction = (extraStyle) => (
    <Pressable
      onPress={action.onPress}
      accessibilityRole="button"
      accessibilityLabel={action.label}
      style={({ hovered }) => [styles.action, hovered && styles.actionHovered, extraStyle]}
    >
      {action.icon ? <Icon name={action.icon} size={12} color={colors.text_color_primary} /> : null}
      <Text size="s" weight="semibold" numberOfLines={1}>{action.label}</Text>
    </Pressable>
  );

  return (
    <View style={styles.wrap}>
      <View style={styles.topRow}>
        <View style={{ flex: 1, minWidth: 0 }}>
          {eyebrow ? (
            <View style={styles.eyebrowRow}>
              <View style={[styles.eyebrowDot, { backgroundColor: eyebrowColor }]} />
              <Text size="xs" weight="bold" style={[styles.eyebrow, { color: eyebrowColor }]}>
                {eyebrow}
              </Text>
            </View>
          ) : null}
          <Text tag="h1" weight="extraBold" style={styles.headline}>
            {headline}
          </Text>
          {subtitle ? (
            <Text tag="p" color="secondary" style={styles.subtitle}>
              {subtitle}
            </Text>
          ) : null}
          {showAction && isXS ? renderAction(styles.actionMobile) : null}
        </View>
        {showAction && !isXS ? renderAction() : null}
      </View>

      {showSearch ? (
        <View
          style={[
            styles.search,
            {
              borderColor: focused ? colors.text_color_primary : colors.border_color_secondary,
              ...(isWeb && focused && { boxShadow: '0 12px 32px -20px rgba(0,0,0,0.35)' }),
            },
          ]}
        >
          <Icon name="magnifying-glass" size={isXS ? 15 : 17} color={focused ? colors.text_color_primary : colors.text_color_tertiary} />
          <TextInput
            value={search.value ?? ''}
            onChangeText={search.onChangeText}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={search.placeholder}
            placeholderTextColor={colors.text_color_tertiary}
            style={styles.searchInput}
            autoCorrect={false}
            autoCapitalize="none"
            returnKeyType="search"
            testID={search.testID}
            clearButtonMode={Platform.OS === 'ios' ? 'while-editing' : undefined}
          />
          {hasValue ? (
            <Pressable
              onPress={clearSearch}
              style={({ hovered }) => [styles.clearBtn, hovered && { backgroundColor: colors.solid_color_tertiary }]}
              accessibilityRole="button"
              accessibilityLabel="Clear search"
            >
              <Icon name="xmark" size={14} color={colors.text_color_secondary} />
            </Pressable>
          ) : (!isXS && countLabel) ? (
            <Text size="s" color="tertiary" style={styles.searchCount}>{countLabel}</Text>
          ) : null}
        </View>
      ) : null}

      {tiles.length > 1 ? (
        isXS ? (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.tilesScroll}
            contentContainerStyle={styles.tilesScrollContent}
          >
            {tiles}
          </ScrollView>
        ) : (
          <View style={styles.tilesWrap}>{tiles}</View>
        )
      ) : null}
    </View>
  );
}

export default React.memo(ToolsHero);
